import { pageZoom } from './zoom.js';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';

/**
 * A panel under (or above) an anchor, for a date or a time on a wide window. It is fixed, so a
 * sheet or a card around the anchor never clips it. Escape and a click outside close it, and a
 * resize or a scroll elsewhere does too.
 */
export function AnchoredPanel({
  anchor,
  label,
  onClose,
  width = 320,
  children,
}: {
  anchor: RefObject<HTMLElement>;
  label: string;
  onClose: () => void;
  width?: number;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<CSSProperties>({ visibility: 'hidden' });
  useLayoutEffect(() => {
    const a = anchor.current;
    const p = panel.current;
    if (a === null || p === null) return;
    const z = pageZoom();
    const r = a.getBoundingClientRect();
    const rect = { left: r.left / z, top: r.top / z, bottom: r.bottom / z };
    const viewH = window.innerHeight / z;
    const height = p.offsetHeight;
    const up = viewH - rect.bottom < height + 8 && rect.top > height + 8;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth / z - width - 8));
    setPlace({
      left,
      width,
      ...(up ? { bottom: viewH - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, [anchor, width]);
  useEffect(() => {
    panel.current?.querySelector<HTMLElement>('button:not(:disabled), input')?.focus();
    const away = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!panel.current?.contains(target) && !anchor.current?.contains(target)) onClose();
    };
    const leave = (event: Event) => {
      if (!panel.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('mousedown', away);
    window.addEventListener('resize', leave);
    window.addEventListener('scroll', leave, true);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('resize', leave);
      window.removeEventListener('scroll', leave, true);
    };
  }, [anchor, onClose]);
  return (
    <div
      ref={panel}
      role="dialog"
      aria-label={label}
      className="k-anchored"
      style={place}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        }
      }}
    >
      {children}
    </div>
  );
}
